import { useEffect, useState } from 'react';
import axios from 'axios';
import socket from '../socket';

const CACHE_MAX_MS = 10 * 60 * 1000;
const cache = new Map();
const subscribers = new Set();
const cacheKey = (token, from, to) => `${token}|${from}|${to}`;
const readCache = (token, from, to) => {
    const hit = cache.get(cacheKey(token, from, to));
    return hit && Date.now() - hit.at < CACHE_MAX_MS ? hit : null;
};

// Keep listening while the dashboard is unmounted so revisiting it cannot
// reuse data invalidated by changes elsewhere in the app or by another user.
const invalidateCache = () => {
    cache.clear();
    subscribers.forEach(refresh => refresh());
};
socket.on('salesUpdated', invalidateCache);
socket.on('connect', invalidateCache);

export default function useDashboardSales(fromDate, toDate) {
    const token = localStorage.getItem('token');
    const [salesEntries, setSalesEntries] = useState(() => readCache(token, fromDate, toDate)?.data ?? []);
    const [loading, setLoading] = useState(() => !readCache(token, fromDate, toDate));
    const [error, setError] = useState('');

    useEffect(() => {
        const cached = readCache(token, fromDate, toDate);
        let hasData = Boolean(cached);
        let disposed = false;
        let requestId = 0;
        let refreshTimer;

        // Discard other sessions' entries and never cache a late response under
        // a different user's token.
        for (const key of cache.keys()) {
            if (!key.startsWith(`${token}|`)) cache.delete(key);
        }

        const fetchSalesEntries = async () => {
            const id = ++requestId;
            const isCurrent = () => !disposed && id === requestId && token === localStorage.getItem('token');
            setLoading(!hasData);
            setError('');
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sales/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { fromDate, toDate },
                });
                if (!isCurrent()) return;
                cache.set(cacheKey(token, fromDate, toDate), { data: response.data, at: Date.now() });
                hasData = true;
                setSalesEntries(response.data);
            } catch (error) {
                if (!isCurrent()) return;
                if (error.response?.data?.logout === true) {
                    cache.clear();
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setError(hasData ? 'Failed to refresh sales entries. Showing previous data.' : 'Failed to fetch sales entries');
            } finally {
                if (isCurrent()) setLoading(false);
            }
        };

        const scheduleRefresh = () => {
            // Invalidate an in-flight response immediately, even during the
            // debounce window. A save can emit once for each associated test.
            requestId += 1;
            clearTimeout(refreshTimer);
            refreshTimer = setTimeout(fetchSalesEntries, 300);
        };
        subscribers.add(scheduleRefresh);

        setError('');
        if (cached) {
            setSalesEntries(cached.data);
            setLoading(false);
        } else {
            fetchSalesEntries();
        }

        return () => {
            disposed = true;
            clearTimeout(refreshTimer);
            subscribers.delete(scheduleRefresh);
        };
    }, [token, fromDate, toDate]);

    return { salesEntries, loading, error };
}
